import { Fragment, Suspense, lazy, useEffect, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useStore, undo, redo, getState } from '../logik/store';
import { byId, processPath, openDecisions, search } from '../logik/model.js';
import * as A from '../logik/actions.js';
import { api, abmeldenUrl, type Archiv, type AufgabeBezugTyp, type CockpitDaten, type Daten, type Suchtreffer } from '../api';
import { filterAusSuche, filterSuche, heuteIso, optionenAusSuche, optionenSuche } from '../aufgaben';
import { aufgabenAnsichtLaden } from '../ansichtGemerkt';
import { abfrageSetzen, abfrageWert } from '../adresse';
import { dokFilterAusSuche, dokFilterSuche } from '../dokumente';
import { sync, onSync, darf, projektOeffnen, projektSchliessen, jetztSpeichern, verwerfen, archivSetzen } from '../sync';
import { useIch } from '../App';
import { DiagramView } from './DiagramView';
import { Panel, schrittLoeschen, verbindungLoeschen } from './Panel';
import { Landkarte } from './Landkarte';
import { DecisionsPage } from './Decisions';
import { Settings } from './Settings';
import { themaFuer } from '../hilfe/hilfe';
import { Avatar } from './Avatar';
import { Benutzermenue } from './Benutzermenue';
import { effectiveTheme, toggleTheme } from '../theme';
import { Icon } from './Icon';
import { Glocke } from './Glocke';
import { useHandy, passt, HANDY } from './geraet';
import { toast } from './toast';
import { download, safeName } from '../logik/io.js';

// ---------- Routen innerhalb eines Projekts ----------
// /p/:projektId                      Prozesslandkarte
// /p/:projektId/entscheidungen       Entscheidungen (?status=offen|entschieden|alle)
// /p/:projektId/cockpit             Projekt-Cockpit: Kennzahlen, Statusbericht, Besprechung importieren
// /p/:projektId/prozess/:pid         Diagramm
// /p/:projektId/prozess/:pid/n/:id   … mit ausgewähltem Schritt (e/:id = Verbindung)
// /p/:projektId/aufgaben             Aufgaben als Liste (?ansicht=board: als Board, ?ansicht=gantt: als Gantt-Plan, ?ansicht=matrix: Eisenhower-Matrix)
// /p/:projektId/aufgaben/:aid        … mit geöffneter Aufgabe
// /p/:projektId/meilensteine          Meilensteine und Phasen (Liste und Zeitleiste)
// /p/:projektId/offene-punkte[/:oid]  Offene Punkte: Risiken, Annahmen, Klärungspunkte (… mit geöffnetem Punkt)
// /p/:projektId/checklisten          Go-Live-/Schulungs-Checklisten
// /p/:projektId/tests                Tests: Testfälle je Prozessschritt mit Fortschritt (?status=…)
// /p/:projektId/abnahmen             Abnahmen: freigegebene Stände, Vergleich (?links=&rechts=&prozess=)
// /p/:projektId/stand/:nr[/prozess/:pid]  Früherer Stand eines Prozesses (nur Ansicht), Unterschiede zu heute
// /p/:projektId/dokumente            Dokumente des Projekts (?q=&ort=&typ=&verwaist=1&sort=&richtung=)
// /p/:projektId?zu=<typ>&id=<id>      Sprung zu Schritt, Prozess, Entscheidung oder Aufgabe (z. B. aus einer Benachrichtigung)
type Sel = { type: 'node' | 'edge'; id: string } | null;
type Ansicht = { view: 'map' } | { view: 'general' } | { view: 'decisions' } | { view: 'process'; pid: string; sel: Sel } | { view: 'tasks'; aid: string | null; ansicht: AufgabenAnsicht; suche?: string; optionen?: string }
  | { view: 'milestones' } | { view: 'issues'; oid: string | null } | { view: 'cockpit' } | { view: 'abnahmen' } | { view: 'tests' } | { view: 'checklisten' }
  | { view: 'dokumente'; suche?: string } | { view: 'stand'; nr: number; pid: string | null };

function ansichtAus(rest: string, suche = ''): Ansicht {
  const teile = rest.split('/').filter(Boolean).map(decodeURIComponent);
  if (teile[0] === 'allgemein') return { view: 'general' };
  if (teile[0] === 'entscheidungen') return { view: 'decisions' };
  if (teile[0] === 'meilensteine') return { view: 'milestones' };
  if (teile[0] === 'cockpit') return { view: 'cockpit' };
  if (teile[0] === 'abnahmen') return { view: 'abnahmen' };
  if (teile[0] === 'stand' && Number(teile[1]) > 0) return { view: 'stand', nr: Number(teile[1]), pid: teile[2] === 'prozess' && teile[3] ? teile[3] : null };
  if (teile[0] === 'tests') return { view: 'tests' };
  if (teile[0] === 'checklisten') return { view: 'checklisten' };
  if (teile[0] === 'dokumente') return { view: 'dokumente', suche: dokFilterSuche(dokFilterAusSuche(suche)) };
  if (teile[0] === 'offene-punkte') return { view: 'issues', oid: teile[1] || null };
  if (teile[0] === 'aufgaben') {
    const a = new URLSearchParams(suche).get('ansicht');
    const ansicht = a === 'board' || a === 'gantt' || a === 'matrix' ? a : 'liste';
    return { view: 'tasks', aid: teile[1] || null, ansicht, suche: filterSuche(filterAusSuche(suche)), optionen: optionenSuche(optionenAusSuche(suche), ansicht) };
  }
  if (teile[0] === 'prozess' && teile[1]) {
    const id = teile.slice(3).join('/');
    const sel: Sel = teile[2] === 'n' && id ? { type: 'node', id } : teile[2] === 'e' && id ? { type: 'edge', id } : null;
    return { view: 'process', pid: teile[1], sel };
  }
  return { view: 'map' };
}

function pfadZu(projektId: string, a: Ansicht) {
  let p = '/p/' + encodeURIComponent(projektId);
  if (a.view === 'general') p += '/allgemein';
  if (a.view === 'decisions') p += '/entscheidungen';
  if (a.view === 'milestones') p += '/meilensteine';
  if (a.view === 'cockpit') p += '/cockpit';
  if (a.view === 'abnahmen') p += '/abnahmen';
  if (a.view === 'stand') p += '/stand/' + a.nr + (a.pid ? '/prozess/' + encodeURIComponent(a.pid) : '');
  if (a.view === 'tests') p += '/tests';
  if (a.view === 'checklisten') p += '/checklisten';
  if (a.view === 'dokumente') p += '/dokumente' + (a.suche ? '?' + a.suche : '');
  if (a.view === 'issues') p += '/offene-punkte' + (a.oid ? '/' + encodeURIComponent(a.oid) : '');
  if (a.view === 'tasks') {
    // Filter bleiben in der Adresse, damit Links und das Öffnen einer Aufgabe sie behalten
    // ebenso die Einstellungen der Ansicht (Board: Spalten und Sortierung, Gantt: Zoom) – so taugt die Adresse als Lesezeichen
    const q = [a.ansicht !== 'liste' ? 'ansicht=' + a.ansicht : '', optionenSuche(optionenAusSuche(a.optionen ?? ''), a.ansicht), a.suche ?? ''].filter(Boolean).join('&');
    p += '/aufgaben' + (a.aid ? '/' + encodeURIComponent(a.aid) : '') + (q ? '?' + q : '');
  }
  if (a.view === 'process') {
    p += '/prozess/' + encodeURIComponent(a.pid);
    if (a.sel) p += (a.sel.type === 'node' ? '/n/' : '/e/') + encodeURIComponent(a.sel.id);
  }
  return p;
}

// Export-Module (Bilder, Druckansicht, Word mit `docx`) erst beim Export nachladen – hält das Haupt-Bundle klein
const bildExport = () => import('../logik/export/basic.js');
const druckExport = () => import('../logik/export/print.js');
const wordExport = () => import('../logik/export/docx.js');
// Aufgaben (Liste, Board, Detail) ebenfalls erst bei Bedarf
const Aufgaben = lazy(() => import('./Aufgaben'));
import type { AufgabenAnsicht } from './Aufgaben';
import { frage, zumKopieren, archivierenBestaetigen } from './frage';
// Reiter „Dokumente“ (alle Dateien des Projekts mit Ablageorten) ebenfalls erst bei Bedarf
const DokumenteSeite = lazy(() => import('./DokumenteSeite'));
// Projektsteuerung (Meilensteine, offene Punkte) ebenso
const Meilensteine = lazy(() => import('./Meilensteine'));
const Abnahmen = lazy(() => import('./Abnahmen'));
const Stand = lazy(() => import('./Stand'));
const Tests = lazy(() => import('./Tests'));
const HilfeDialog = lazy(() => import('./Hilfe').then((m) => ({ default: m.HilfeDialog })));
const Checklisten = lazy(() => import('./Checklisten'));
const OffenePunkte = lazy(() => import('./OffenePunkte'));
// Projekt-Cockpit (Kennzahlen, Statusbericht, Besprechungsimport) ebenso
const Cockpit = lazy(() => import('./Cockpit'));
// Selten genutzte Dialoge (Versionsverlauf, Mitglieder) ebenfalls erst bei Bedarf
const HistoryDialog = lazy(() => import('./Verlauf').then((m) => ({ default: m.HistoryDialog })));
const MitgliederDialog = lazy(() => import('./Verwaltung').then((m) => ({ default: m.MitgliederDialog })));
const ProtokollDialog = lazy(() => import('./Protokoll'));
const AsanaImportDialog = lazy(() => import('./AsanaImport'));
const BenachrichtigungsEinstellungen = lazy(() => import('./Benachrichtigungen').then((m) => ({ default: m.EinstellungenDialog })));
const KalenderAbo = lazy(() => import('./KalenderAbo'));

const fmtZeit = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
const ROLLE: Record<string, string> = { admin: 'Admin', verantwortlich: 'Prozessverantwortlich', mitwirkend: 'Mitwirkend', lesend: 'Lesend' };

/** React-Hook: rendert neu, wenn sich der Abgleich-Status ändert. */
let syncVersion = 0;
onSync(() => { syncVersion++; });
const useSync = () => {
  useSyncExternalStore(onSync, () => syncVersion);
  return sync;
};

// ---------- Breite des Detailbereichs (ziehbar, je Browser gemerkt) ----------
const PANEL_KEY = 'prozessatlas:panelbreite';
const PANEL_DEFAULT = 400;
const clampPanel = (w: number) => Math.round(Math.max(300, Math.min(w, window.innerWidth * 0.75)));
function readPanelWidth() {
  try { return clampPanel(Number(localStorage.getItem(PANEL_KEY)) || PANEL_DEFAULT); } catch { return PANEL_DEFAULT; }
}
function PanelResizer({ onResize }: { onResize: (w: number) => void }) {
  const down = (e: ReactPointerEvent) => {
    e.preventDefault();
    const move = (ev: PointerEvent) => onResize(clampPanel(window.innerWidth - ev.clientX));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.classList.remove('resizing');
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    document.body.classList.add('resizing');
  };
  return <div className="panel-resizer" title="Ziehen, um den Detailbereich breiter oder schmaler zu machen (Doppelklick: Standardbreite)"
    onPointerDown={down} onDoubleClick={() => onResize(PANEL_DEFAULT)} data-testid="panel-resizer" />;
}

/** Zahl am Reiter; 0 = keine Anzeige. Blau = offen, Rot = etwas überfällig bzw. fehlgeschlagen. */
function Zahl({ n, farbe, titel, testid }: { n: number; farbe: 'blau' | 'rot' | 'orange'; titel: string; testid: string }) {
  return n > 0 ? <span className={'badge-count ' + farbe} title={titel} data-testid={testid}>{n}</span> : null;
}

function Menu({ label, icon, children, testid }: { label: string; icon: string; children: ReactNode; testid?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  return (
    <div className="menu" ref={ref}>
      <button className={'btn' + (open ? ' on' : '')} title={label} onClick={() => setOpen(!open)} data-testid={testid}><Icon name={icon} /><span className="lbl opt">{label}</span></button>
      {open && <div className="pop" onClick={(e) => { if ((e.target as HTMLElement).closest('button')) setOpen(false); }}>{children}</div>}
    </div>
  );
}

const SUCH_ART: Record<string, string> = {
  prozess: 'Prozess', schritt: 'Schritt', entscheidung: 'Entscheidung', aufgabe: 'Aufgabe', kommentar: 'Kommentar', offenerPunkt: 'Offener Punkt',
  meilenstein: 'Meilenstein', testfall: 'Testfall', checklistenPunkt: 'Checkliste', besprechung: 'Besprechung', dokument: 'Dokument',
};

/**
 * Suche in der Kopfleiste: Prozesse, Schritte und Entscheidungen sofort aus den geladenen Daten, dazu (kurz verzögert) vom
 * Server Aufgaben, Kommentare, offene Punkte, Meilensteine, Testfälle, Checklisten, Besprechungen und Dokumentnamen.
 */
function Search({ projektId, data, onPick, onZiel }: { projektId: string; data: Daten; onPick: (r: Daten) => void; onZiel: (ziel: string, id: string | null, titel?: string) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [act, setAct] = useState(0);
  const [server, setServer] = useState<{ q: string; treffer: Suchtreffer[] } | null>(null);
  useEffect(() => {
    const s = q.trim();
    if (!open || s.length < 2) return;
    const t = window.setTimeout(() => {
      api.suche(projektId, s).then((treffer) => setServer({ q: s, treffer })).catch(() => setServer({ q: s, treffer: [] }));
    }, 250);
    return () => window.clearTimeout(t);
  }, [q, open, projektId]);
  const lokal: Daten[] = open ? search(data, q) : [];
  const vomServer = open && server && server.q === q.trim() ? server.treffer : [];
  const res: Daten[] = [...lokal, ...vomServer.map((t) => ({ kind: t.art, titel: t.titel, ausschnitt: t.ausschnitt, ziel: t.ziel, zielId: t.zielId }))];
  const laedt = open && q.trim().length >= 2 && server?.q !== q.trim();
  const pick = (r: Daten) => { setOpen(false); setQ(''); if (r.ziel) onZiel(r.ziel, r.zielId, r.titel); else onPick(r); };
  return (
    <div className="search">
      <Icon name="search" />
      <input placeholder="Suchen …" value={q} data-testid="search"
        onChange={(e) => { setQ(e.target.value); setOpen(true); setAct(0); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { setAct(Math.min(act + 1, res.length - 1)); e.preventDefault(); }
          if (e.key === 'ArrowUp') { setAct(Math.max(act - 1, 0)); e.preventDefault(); }
          if (e.key === 'Enter' && res[act]) pick(res[act]);
          if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); }
        }} />
      {open && q.trim().length >= 2 && (
        <div className="results">
          {res.length === 0 && <div className="empty">{laedt ? 'Wird gesucht …' : 'Keine Treffer.'}</div>}
          {res.map((r, i) => (
            <button key={i} className={i === act ? 'act' : ''} onMouseDown={(e) => { e.preventDefault(); pick(r); }} data-testid={'treffer-' + r.kind}>
              <div className="k">{SUCH_ART[r.kind] ?? r.kind}{r.p && r.kind !== 'prozess' ? ' · ' + r.p.titel : ''}</div><div>{r.titel}</div>
              {r.ausschnitt && <div className="ausschnitt">{r.ausschnitt}</div>}
            </button>
          ))}
          {res.length > 0 && laedt && <div className="empty">Weitere Treffer werden gesucht …</div>}
        </div>
      )}
    </div>
  );
}

/** Speichern über Knopf oder Strg+S: ein Feld mit Fokus übernimmt seinen Wert erst beim Verlassen. */
const speichern = () => {
  (document.activeElement as HTMLElement | null)?.blur?.();
  void jetztSpeichern();
};

/** Speicherstand neben dem Titel. */
function SyncStatus({ dirty }: { dirty: boolean }) {
  const s = useSync();
  if (s.status === 'fehler') return <button className="sync err" onClick={speichern} title={s.fehler} data-testid="sync-status">Fehler – erneut speichern</button>;
  if (s.archiv) return <span className="sync" data-testid="sync-status" title="Archivierte Projekte sind nur lesbar">Archiviert · nur lesbar</span>;
  if (!darf('verantwortlich')) return <span className="sync" data-testid="sync-status" title="Ihre Rolle in diesem Projekt">{ROLLE[s.rolle]} · nur Ansicht</span>;
  const txt = s.status === 'speichert' ? 'speichert …' : dirty ? 'ungespeicherte Änderungen' : s.savedAt ? 'gespeichert ' + fmtZeit(s.savedAt) : 'aktuell';
  return <span className="sync" data-testid="sync-status">{txt}</span>;
}

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE');

/** Hinweis unter den Reitern: archiviert, nur lesbar, Löschdatum; Admins können wiederherstellen. */
function ArchivHinweis({ archiv, onWiederherstellen }: { archiv: Archiv; onWiederherstellen?: () => void }) {
  return (
    <div className="archiv-hinweis" role="status" data-testid="archiv-hinweis">
      <Icon name="info" />
      <span>
        <b>Archiviert – nur lesbar.</b> Archiviert am {datum(archiv.am)}{archiv.von ? ` von ${archiv.von}` : ''}; wird am{' '}
        <b data-testid="archiv-loeschen-am">{datum(archiv.loeschenAm)}</b> endgültig gelöscht (Aufbewahrungsfrist {archiv.monate} Monate).
      </span>
      {onWiederherstellen && <button className="btn small" onClick={onWiederherstellen} data-testid="projekt-wiederherstellen"><Icon name="undo" /> Wiederherstellen</button>}
    </div>
  );
}

/** Speichern und Verwerfen in der Kopfleiste – sichtbar, solange es ungespeicherte Änderungen gibt. */
function SpeichernKnoepfe({ dirty }: { dirty: boolean }) {
  const s = useSync();
  if (!dirty && s.status !== 'speichert' && s.status !== 'fehler') return null;
  const laeuft = s.status === 'speichert';
  return (
    <>
      <button className="btn" disabled={laeuft || !dirty} data-testid="verwerfen" title="Ungespeicherte Änderungen verwerfen"
        onClick={async () => { if (await frage('Alle ungespeicherten Änderungen verwerfen?', { ja: 'Verwerfen', gefahr: true })) verwerfen(); }}><Icon name="x" /><span className="lbl">Verwerfen</span></button>
      <button className="btn primary" disabled={laeuft} data-testid="speichern" title="Änderungen speichern (Strg+S)" onClick={speichern}>
        <Icon name="save" /><span className="lbl">{laeuft ? 'Speichert …' : 'Speichern'}</span></button>
    </>
  );
}

/** Rückfrage, wenn der Anwender mit ungespeicherten Änderungen die Bearbeitung verlässt. */
function UngespeichertDialog({ weiter, abbrechen }: { weiter: () => void; abbrechen: () => void }) {
  const s = useSync();
  const sichern = async () => {
    await jetztSpeichern();
    if (!getState().dirty) weiter();
  };
  return (
    <div className="overlay" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Ungespeicherte Änderungen" data-testid="ungespeichert-dialog" style={{ maxWidth: 460 }}>
        <h2>Ungespeicherte Änderungen</h2>
        <p>Sie haben Änderungen, die noch nicht gespeichert sind. Sollen sie gespeichert oder verworfen werden?</p>
        <div className="knoepfe">
          <button className="btn" onClick={abbrechen}>Abbrechen</button>
          <button className="btn" data-testid="ungespeichert-verwerfen" onClick={() => { verwerfen(); weiter(); }}>Verwerfen</button>
          <button className="btn primary" data-testid="ungespeichert-speichern" disabled={s.status === 'speichert'} onClick={() => void sichern()}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

/** Arbeitsbereich eines Projekts: Landkarte, Diagramme, Entscheidungen. */
export function Projekt() {
  const { projektId = '', '*': rest = '' } = useParams();
  const { search } = useLocation();
  const nav = useNavigate();
  const { ich } = useIch();
  const sy = useSync();
  const st = useStore();
  const [ladeFehler, setLadeFehler] = useState('');

  useEffect(() => {
    setLadeFehler('');
    projektOeffnen(projektId).catch((e) => setLadeFehler((e as Error).message));
    // zuletzt gewählte Aufgabenansicht schon holen, damit „Aufgaben“ sie ohne Warten zeigt
    if (ich?.email) void aufgabenAnsichtLaden(ich.email, projektId);
    return () => {
      // Verlassen ohne Rückfrage (z. B. Zurück-Taste des Browsers): Ungespeichertes wird nicht übernommen
      if (getState().dirty) toast('Nicht gespeicherte Änderungen wurden verworfen.', true);
      projektSchliessen();
    };
  }, [projektId]);

  // Ungespeicherte Änderungen: beim Schließen des Tabs warnen
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (getState().dirty) e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  if (ladeFehler)
    return (
      <div className="empty-state" data-testid="projekt-fehler">
        Das Projekt kann nicht geöffnet werden: {ladeFehler}
        <p><Link className="btn" to="/">Zur Projektübersicht</Link></p>
      </div>
    );
  if (sy.projektId !== projektId || sy.status === 'laedt' || !st.data) return <div className="empty-state">Projekt wird geladen …</div>;
  const q = new URLSearchParams(search);
  const sprung = q.get('zu') && q.get('id') ? { typ: q.get('zu')!, id: q.get('id')! } : null;
  return <Arbeitsbereich projektId={projektId} ansicht={ansichtAus(rest, search)} sprung={sprung}
    go={(a, ersetzen) => nav(pfadZu(projektId, a), { replace: !!ersetzen })} istSuperadmin={!!ich?.superadmin} />;
}

function Arbeitsbereich({ projektId, ansicht, sprung, go, istSuperadmin }: {
  projektId: string; ansicht: Ansicht; sprung: { typ: string; id: string } | null; go: (a: Ansicht, ersetzen?: boolean) => void; istSuperadmin: boolean;
}) {
  const st = useStore();
  const sy = useSync();
  const data = st.data;
  const darfBearbeiten = darf('verantwortlich');
  const nav = useNavigate();
  // Admin des Projekts (auch im archivierten Projekt: Mitglieder und Protokoll ansehen, wiederherstellen)
  const istAdmin = sy.rolle === 'admin' || istSuperadmin;
  const archiv = sy.archiv;
  const [editAn, setEdit] = useState(false);
  const edit = editAn && darfBearbeiten;
  // Zielbild-Schalter steht in der Adresse (?zielbild=1), solange Prozesse oder ein Diagramm zu sehen sind
  const [zielbild, setZielbild] = useState(() => abfrageWert('zielbild', ['1']) === '1');
  const mitZielbild = ansicht.view === 'map' || ansicht.view === 'general' || ansicht.view === 'process';
  useEffect(() => { if (mitZielbild) abfrageSetzen({ zielbild: zielbild ? '1' : null }); });
  const handy = useHandy();
  // Handy: Detailbereich ist ein Vollbild-Blatt und startet geschlossen; Tippen auf einen Schritt öffnet es
  const [panel, setPanel] = useState(() => !passt(HANDY));
  const [mehr, setMehr] = useState(false); // Überlaufmenü der Kopfleiste (Handy)
  const [weitere, setWeitere] = useState(false); // Menü „⋯“ mit selteneren Funktionen (Desktop)
  // Zahlen für die Reiter (offen/überfällig) aus dem Cockpit: neu bei jedem Wechsel der Ansicht und beim Zurückkehren ins Fenster
  const [zahlen, setZahlen] = useState<CockpitDaten | null>(null);
  useEffect(() => {
    let aktiv = true;
    const laden = () => api.cockpit(projektId, heuteIso()).then((z) => aktiv && setZahlen(z)).catch(() => { /* ohne Zahlen weiter */ });
    void laden();
    window.addEventListener('focus', laden);
    return () => { aktiv = false; window.removeEventListener('focus', laden); };
  }, [projektId, ansicht.view]);
  const [suche, setSuche] = useState(false); // Suchzeile (Handy)
  const [panelW, setPanelW] = useState(readPanelWidth);
  const resizePanel = (w: number) => {
    setPanelW(w);
    try { localStorage.setItem(PANEL_KEY, String(w)); } catch { /* egal */ }
  };
  const [verlaufPid, setVerlaufPid] = useState<string | null>(null); // Versionsverlauf nur für diesen Prozess
  const [dialog, setDialog] = useState<null | 'history' | 'settings' | 'help' | 'mitglieder' | 'protokoll' | 'benachrichtigungen' | 'kalender' | 'asana'>(null);
  // nach einem Import aus Asana die Aufgaben neu laden (neuer Schlüssel = neu einlesen)
  const [aufgabenStand, setAufgabenStand] = useState(0);
  // Rückfrage bei ungespeicherten Änderungen: was danach geschehen soll
  const [nachFrage, setNachFrage] = useState<null | { weiter: () => void }>(null);
  // Hilfe öffnet beim Thema der Ansicht bzw. des Dialogs, aus dem sie aufgerufen wird
  const [hilfeThema, setHilfeThema] = useState('');
  const hilfeOeffnen = () => {
    setHilfeThema(themaFuer({ view: ansicht.view, dialog: dialog === 'help' ? null : dialog, bearbeiten: edit, auswahl: ansicht.view === 'process' && !!ansicht.sel }));
    setDialog('help');
  };
  const erstFragen = (weiter: () => void) => (getState().dirty ? setNachFrage({ weiter }) : weiter());
  const { ich, neuLaden } = useIch();
  const [, setTheme] = useState(effectiveTheme());

  const archivieren = async () => {
    if (getState().dirty) { toast('Bitte zuerst speichern oder verwerfen.', true); return; }
    if (!(await archivierenBestaetigen(data.meta.titel))) return;
    try {
      archivSetzen(await api.projektArchivieren(projektId));
      setEdit(false);
      toast('Projekt archiviert – nur lesbar.');
      neuLaden();
    } catch (e) { toast((e as Error).message, true); }
  };
  const wiederherstellen = async () => {
    try {
      await api.projektWiederherstellen(projektId);
      archivSetzen(null);
      toast('Projekt wiederhergestellt.');
      neuLaden();
    } catch (e) { toast((e as Error).message, true); }
  };

  const proc = ansicht.view === 'process' ? byId(data.processes, ansicht.pid) : null;
  const sel: Sel = ansicht.view === 'process' ? ansicht.sel : null;
  // Handy: ohne Auswahl (Tippen ins Leere, anderer Prozess, Zurück-Taste) schließt das Blatt
  useEffect(() => { if (handy && !sel) setPanel(false); }, [handy, sel?.id, proc?.id]);
  // Überlaufmenü schließt bei Tippen daneben
  useEffect(() => {
    if (!mehr) return;
    const zu = (e: PointerEvent) => { if (!(e.target as Element).closest?.('.tools-mehr, [data-testid=kopf-mehr]')) setMehr(false); };
    document.addEventListener('pointerdown', zu);
    return () => document.removeEventListener('pointerdown', zu);
  }, [mehr]);
  useEffect(() => {
    if (!weitere) return;
    const zu = (e: PointerEvent) => { if (!(e.target as Element).closest?.('.kopf-selten, [data-testid=kopf-weitere]')) setWeitere(false); };
    document.addEventListener('pointerdown', zu);
    return () => document.removeEventListener('pointerdown', zu);
  }, [weitere]);

  // Navigationspfad (Brotkrumen): folgt dem Weg des Anwenders, nicht nur der Prozesshierarchie.
  const trail = useRef<{ pid: string | null; ids: string[] }>({ pid: null, ids: [] });
  const jumpRef = useRef<string | null>(null);
  const [krumenAuf, setKrumenAuf] = useState<string | null>(null); // Pfad, dessen mittlere Stufen aufgeklappt sind
  // Nach dem Sprung aus einer Aufgabe zu ihrem Bezug: Weg zurück zur Aufgabe (in der Brotkrumen-Leiste)
  const [zurueck, setZurueck] = useState<{ ansicht: Ansicht; titel: string } | null>(null);
  useEffect(() => { if (ansicht.view === 'tasks' || ansicht.view === 'map') setZurueck(null); }, [ansicht.view]);
  // „Neues Projekt › Aus Asana“ öffnet das neue Projekt mit ?asana=1: gleich den Importdialog zeigen
  useEffect(() => {
    if (abfrageWert('asana', ['1']) !== '1') return;
    abfrageSetzen({ asana: null });
    if (istAdmin && !archiv) setDialog('asana');
  }, [istAdmin]);
  if (trail.current.pid !== (proc?.id || null)) {
    const prev = trail.current.ids.filter((id) => byId(data.processes, id));
    const pid = proc?.id || null;
    let ids: string[];
    if (!pid) ids = [];
    else if (prev.includes(pid)) ids = prev.slice(0, prev.indexOf(pid) + 1);
    else {
      const path = processPath(data, pid).map((p: Daten) => p.id);
      const last = prev[prev.length - 1];
      const k = last ? path.indexOf(last) : -1;
      if (k >= 0) ids = [...prev, ...path.slice(k + 1)]; // Abstieg in der Hierarchie
      else if (last && jumpRef.current === pid) ids = [...prev, pid]; // Sprung in anderen Prozess
      else ids = path; // direkter Aufruf (Landkarte, Suche, Link)
    }
    jumpRef.current = null;
    trail.current = { pid, ids };
  }

  // Ungültige Routen (z. B. nach Löschen durch andere) bereinigen
  useEffect(() => {
    if (ansicht.view === 'process' && !proc) go({ view: 'map' });
    else if (ansicht.view === 'process' && sel && !(sel.type === 'node' ? byId(data.nodes, sel.id) : byId(data.edges, sel.id))) go({ ...ansicht, sel: null });
  });

  const select = (s: Sel) => {
    if (ansicht.view !== 'process') return;
    go({ view: 'process', pid: ansicht.pid, sel: s });
    if (s) setPanel(true);
  };
  const drill = (pid: string | null) => go(pid ? { view: 'process', pid, sel: null } : { view: 'map' });
  // Sprung über einen Unterprozess-Kasten: Ziel wird an den Navigationspfad angehängt
  const jump = (pid: string | null) => {
    jumpRef.current = pid;
    drill(pid);
  };
  const navigate = (pid: string, nodeId?: string | null) => {
    go({ view: 'process', pid, sel: nodeId ? { type: 'node', id: nodeId } : null });
    setPanel(true);
  };

  // Auswahl aus der aktuellen Adresse: direkt nach einem Klick ist die Adresse schon neu, die Darstellung noch nicht
  const aktuelleAuswahl = (): Sel => {
    const vorn = '/p/' + encodeURIComponent(projektId);
    const a = location.pathname.startsWith(vorn) ? ansichtAus(location.pathname.slice(vorn.length)) : null;
    return a ? (a.view === 'process' ? a.sel : null) : sel;
  };

  // Tastenkürzel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inField = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '');
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); if (darfBearbeiten) speichern(); return; }
      if (e.key === 'F1') { e.preventDefault(); hilfeOeffnen(); return; }
      if (inField) return;
      if (darfBearbeiten && mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (darfBearbeiten && mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      else if (e.key === 'Escape') { if (nachFrage) setNachFrage(null); else if (dialog) setDialog(null); else if (mehr) setMehr(false); else if (sel) select(null); }
      // Entf löscht nach Rückfrage; die Auswahl fällt nur weg, wenn wirklich gelöscht wurde (läuft schon eine Rückfrage, nichts tun)
      else if (edit && (e.key === 'Delete' || e.key === 'Backspace') && aktuelleAuswahl() && !document.querySelector('.frage-overlay')) {
        e.preventDefault();
        const s = aktuelleAuswahl()!;
        void (async () => {
          const n = s.type === 'edge' ? null : byId(data.nodes, s.id);
          const weg = s.type === 'edge' ? await verbindungLoeschen(data, s.id) : n ? await schrittLoeschen(n) : false;
          if (weg) select(null);
        })();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const run = (fn: () => unknown) => async () => {
    try { await fn(); } catch (e) { console.error(e); toast('Export fehlgeschlagen: ' + (e as Error).message, true); }
  };
  // Vollständiger Export vom Server (enthält alles, was in der Datenbank steht – im Format des Imports)
  const serverExport = run(async () => {
    const d = await api.exportieren(projektId);
    download(new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' }), safeName(d.meta?.titel || projektId) + '.json');
  });

  // Jede Ansicht hat eine eigene URL (Projekt, Prozess, Schritt, Entscheidungen); nach der Anmeldung geht es genau dorthin
  const linkKopieren = async () => {
    try { await navigator.clipboard.writeText(location.href); toast('Link kopiert.'); }
    catch { await zumKopieren('Die Zwischenablage ist gesperrt – Link zu dieser Ansicht:', location.href); }
  };
  const pid = proc?.id || null;
  const crumbs = trail.current.ids.map((id) => byId(data.processes, id)).filter(Boolean);
  // Lange Pfade: erste Stufe, „…“ und die letzten zwei – ein Klick auf „…“ zeigt alle Stufen dieses Pfads
  const sichtbareKrumen: (Daten | null)[] = crumbs.length > 4 && krumenAuf !== pid ? [crumbs[0], null, ...crumbs.slice(-2)] : crumbs;
  const openCount = openDecisions(data).length;
  const z = zahlen;
  const offeneTests = z ? z.tests.offen + z.tests.fehler + z.tests.blockiert : 0;
  const dark = effectiveTheme() === 'dark';

  // Ansicht zu einem Bezug (Prozess, Schritt, Entscheidung, Aufgabe); null = gibt es nicht (mehr)
  const bezugAnsicht = (typ: string, id: string): Ansicht | null => {
    if (typ === 'prozess') return byId(data.processes, id) ? { view: 'process', pid: id, sel: null } : null;
    if (typ === 'schritt') { const n = byId(data.nodes, id); return n ? { view: 'process', pid: n.processId, sel: { type: 'node', id } } : null; }
    if (typ === 'entscheidung') {
      const d = byId(data.decisions, id);
      if (!d) return null;
      return d.nodeId && byId(data.nodes, d.nodeId)
        ? { view: 'process', pid: d.processId ?? byId(data.nodes, d.nodeId).processId, sel: { type: 'node', id: d.nodeId } }
        : { view: 'decisions' };
    }
    if (typ === 'aufgabe') return { view: 'tasks', aid: id, ansicht: 'liste' };
    if (typ === 'offenerPunkt') return { view: 'issues', oid: id };
    if (typ === 'projekt') return { view: 'map' };
    return null;
  };
  // Sprung aus der Suche (Server-Treffer): Bezug oder Ansicht
  const sucheZiel = (ziel: string, id: string | null, titel?: string) => {
    const ansichten: Record<string, Ansicht> = { meilensteine: { view: 'milestones' }, checklisten: { view: 'checklisten' }, tests: { view: 'tests' }, cockpit: { view: 'cockpit' } };
    // Dokument am Projekt: Reiter „Dokumente“, gefiltert auf den Dateinamen
    if (ziel === 'dokumente') { go({ view: 'dokumente', suche: titel ? dokFilterSuche({ ...dokFilterAusSuche(''), text: titel }) : '' }); return; }
    const a = ansichten[ziel] ?? (id ? bezugAnsicht(ziel, id) : null);
    if (!a) { toast('Das Ziel gibt es nicht mehr.', true); return; }
    go(a);
    if (a.view === 'process' && a.sel) setPanel(true);
  };
  // Sprung aus einer Aufgabe zu ihrem Bezug
  const zuBezug = (typ: AufgabeBezugTyp, id: string, aufgabeTitel?: string) => {
    const a = bezugAnsicht(typ, id);
    if (!a) { toast('Das Ziel gibt es nicht mehr.', true); return; }
    const vorher = ansicht;
    go(a);
    if (vorher.view === 'tasks' && vorher.aid && a.view !== 'tasks') setZurueck({ ansicht: vorher, titel: aufgabeTitel ?? 'Aufgabe' });
    if (a.view === 'process' && a.sel) setPanel(true);
  };
  // Sprung über ?zu=<typ>&id=<id> (z. B. aus der Glocke): ersetzt den Eintrag im Verlauf durch die Zielansicht
  useEffect(() => {
    if (!sprung) return;
    const a = bezugAnsicht(sprung.typ, sprung.id);
    if (!a) toast('Das Ziel gibt es nicht mehr.', true);
    else if (a.view === 'process' && a.sel) setPanel(true);
    go(a ?? { view: 'map' }, true);
  }, [sprung?.typ, sprung?.id]);

  let main: ReactNode;
  if (ansicht.view === 'tasks' || ansicht.view === 'milestones' || ansicht.view === 'issues' || ansicht.view === 'cockpit' || ansicht.view === 'abnahmen' || ansicht.view === 'stand' || ansicht.view === 'tests' || ansicht.view === 'checklisten' || ansicht.view === 'dokumente') main = null;
  else if (ansicht.view === 'decisions') main = <DecisionsPage data={data} onNavigate={navigate} darfBearbeiten={darfBearbeiten} />;
  else if (proc) main = <DiagramView data={data} pid={proc.id} sel={sel} edit={edit} zielbild={zielbild} onSelect={select} onDrill={jump} onBearbeiten={() => setEdit(true)} />;
  else main = <Landkarte data={data} edit={edit} onOpen={drill} onDecisions={() => go({ view: 'decisions' })} onHistory={() => { setVerlaufPid(null); setDialog('history'); }}
    teil={ansicht.view === 'general' ? 'allgemein' : 'prozesse'} />;

  return (
    <div className="app">
      <header className={'topbar' + (suche ? ' suche-offen' : '')}>
        <div className="brand" title="Zur Projektübersicht">
          <Link to="/" onClick={(e) => { if (getState().dirty) { e.preventDefault(); erstFragen(() => nav('/')); } }}><img className="logo-iwm-light" src="/iwm_logo.png" alt="IWM" /><img className="logo-iwm-dark" src="/iwm_logo_dunkel.png" alt="IWM" /></Link>
          {data.meta.logoKunde && <><span className="sep" /><img className="logo-kunde" src={data.meta.logoKunde} alt={data.meta.kunde} onClick={() => go({ view: 'map' })} /></>}
        </div>
        <div className="titles">
          <div className="t" title={data.meta.titel}>{data.meta.titel} <SyncStatus dirty={st.dirty} /></div>
        </div>
        <div className="tools">
          <Search projektId={projektId} data={data} onZiel={(ziel, id, titel) => { setSuche(false); sucheZiel(ziel, id, titel); }} onPick={(r) => (setSuche(false), r.kind === 'prozess' ? drill(r.p.id) : r.kind === 'schritt' ? navigate(r.p.id, r.n.id) : r.d.nodeId ? navigate(r.d.processId, r.d.nodeId) : go({ view: 'decisions' }))} />
          <button className={'btn icon nur-handy' + (suche ? ' on' : '')} title="Suchen" data-testid="kopf-suche"
            onClick={() => { setSuche(!suche); if (!suche) setTimeout(() => document.querySelector<HTMLInputElement>('[data-testid=search]')?.focus()); }}><Icon name="search" /></button>
          {darfBearbeiten && <SpeichernKnoepfe dirty={st.dirty} />}
          <div className={'tools-mehr' + (mehr ? ' offen' : '')} data-testid="kopf-menue"
            onClick={(e) => { const b = (e.target as Element).closest('button, a'); if (b && !b.parentElement?.classList.contains('menu')) setMehr(false); }}>
          {edit && (
            <>
              <button className="btn icon" title="Rückgängig (Strg+Z)" disabled={!st.past.length} onClick={undo}><Icon name="undo" /><span className="lbl nur-mehr">Rückgängig</span></button>
              <button className="btn icon" title="Wiederholen (Strg+Y)" disabled={!st.future.length} onClick={redo}><Icon name="redo" /><span className="lbl nur-mehr">Wiederholen</span></button>
            </>
          )}
          {darfBearbeiten && (
            <button className={'btn' + (edit ? ' on' : '')} onClick={() => (edit ? erstFragen(() => setEdit(false)) : setEdit(true))} data-testid="toggle-edit" title="Bearbeitungsmodus"><Icon name={edit ? 'eye' : 'edit'} /><span className="lbl">{edit ? 'Ansehen' : 'Bearbeiten'}</span></button>
          )}
          <button className={'btn' + (zielbild ? ' on' : '')} onClick={() => setZielbild(!zielbild)} title="Zielbild / Ausbaustufe 2 hervorheben" data-testid="toggle-zielbild"><Icon name="layers" /><span className="lbl opt">Zielbild</span></button>
          <Menu label="Export" icon="download" testid="menu-export">
            {pid && (
              <>
                <div className="h">Aktueller Prozess (inkl. Unterprozesse)</div>
                <button data-testid="exp-pdf" onClick={run(async () => (await druckExport()).printReport(data, pid, { zielbild }))}>PDF / Drucken</button>
                <button data-testid="exp-docx" onClick={run(async () => (await wordExport()).exportDocx(data, pid, { zielbild }))}>Word (.docx)</button>
                <button data-testid="exp-png" onClick={run(async () => (await bildExport()).exportPng(data, pid, { zielbild }))}>Bild PNG (nur Diagramm)</button>
                <button data-testid="exp-svg" onClick={run(async () => (await bildExport()).exportSvg(data, pid, { zielbild }))}>Bild SVG (nur Diagramm)</button>
                <button data-testid="exp-json" onClick={run(async () => (await bildExport()).exportJson(data, pid))}>JSON (Daten dieses Prozesses)</button>
                <hr />
              </>
            )}
            <div className="h">Gesamte Prozessdokumentation</div>
            <button data-testid="exp-pdf-all" onClick={run(async () => (await druckExport()).printReport(data, null, { zielbild }))}>PDF / Drucken</button>
            <button data-testid="exp-docx-all" onClick={run(async () => (await wordExport()).exportDocx(data, null, { zielbild }))}>Word (.docx)</button>
            <button data-testid="exp-json-all" onClick={serverExport}>JSON (alle Daten)</button>
            <hr />
            <button onClick={() => { setVerlaufPid(null); setDialog('history'); }} data-testid="open-history">Versionsverlauf …{data.meta.version ? ' (Version ' + data.meta.version + ')' : ''}</button>
          </Menu>
          <button className="btn icon" title="Link zu dieser Ansicht kopieren (Projekt, Prozess, Schritt oder Aufgabe)" onClick={linkKopieren} data-testid="link-kopieren"><Icon name="link" /><span className="lbl nur-mehr">Link kopieren</span></button>
          {/* Seltenere Funktionen: am Desktop unter „⋯“, auf Handy/Tablet direkt im Überlaufmenü */}
          <button className={'btn icon nicht-kompakt' + (weitere ? ' on' : '')} title="Weitere Funktionen" aria-expanded={weitere} onClick={() => setWeitere(!weitere)} data-testid="kopf-weitere"><Icon name="more" /></button>
          <div className={'kopf-selten' + (weitere ? ' offen' : '')} onClick={(e) => { if ((e.target as Element).closest('button, a')) setWeitere(false); }}>
            {istAdmin && <button className="btn" title="Einstellungen" onClick={() => setDialog('settings')} data-testid="open-settings"><Icon name="gear" /><span className="lbl">Einstellungen</span></button>}
            {istAdmin && <button className="btn" title="Mitglieder und Rollen" onClick={() => setDialog('mitglieder')} data-testid="open-mitglieder"><Icon name="users" /><span className="lbl">Mitglieder</span></button>}
            {istAdmin && <button className="btn" title="Protokoll: wer hat was wann geändert oder heruntergeladen" onClick={() => setDialog('protokoll')} data-testid="open-protokoll"><Icon name="liste" /><span className="lbl">Protokoll</span></button>}
            {istAdmin && !archiv && <button className="btn" title="Abschnitte, Aufgaben, Kommentare und Dateien aus einem Asana-Projekt übernehmen – ohne E-Mails" onClick={() => setDialog('asana')} data-testid="open-asana"><Icon name="upload" /><span className="lbl">Aus Asana importieren …</span></button>}
            <button className="btn" title="Hilfe zur aktuellen Ansicht (F1)" onClick={hilfeOeffnen} data-testid="open-hilfe"><Icon name="help" /><span className="lbl">Hilfe</span></button>
            <Link className="btn" to="/" title="Zur Projektübersicht" data-testid="zur-uebersicht" onClick={(e) => { if (getState().dirty) { e.preventDefault(); erstFragen(() => nav('/')); } }}><Icon name="map" /><span className="lbl">Zur Projektübersicht</span></Link>
            {/* Benachrichtigungen, Kalender und Design sitzen am Desktop im Benutzermenü; auf Handy/Tablet ist das Benutzermenü ausgeblendet */}
            <button className="btn nur-kompakt" title="Benachrichtigungen einstellen" onClick={() => setDialog('benachrichtigungen')} data-testid="kopf-benachrichtigungen"><Icon name="bell" /><span className="lbl">Benachrichtigungen einstellen …</span></button>
            <button className="btn nur-kompakt" title="Kalender abonnieren" onClick={() => setDialog('kalender')} data-testid="kopf-kalender"><Icon name="kalender" /><span className="lbl">Kalender abonnieren …</span></button>
            <button className="btn nur-kompakt" title={dark ? 'Helles Design' : 'Dunkles Design'} onClick={() => toggleTheme(setTheme)} data-testid="toggle-theme"><Icon name={dark ? 'sun' : 'moon'} /><span className="lbl">{dark ? 'Helles Design' : 'Dunkles Design'}</span></button>
            <a className="btn nur-kompakt" href={abmeldenUrl()}><Icon name="x" /><span className="lbl">Abmelden</span></a>
          </div>
          </div>
          <Glocke />
          <button className={'btn icon nur-kompakt' + (mehr ? ' on' : '')} title="Weitere Funktionen" aria-expanded={mehr} onClick={() => setMehr(!mehr)} data-testid="kopf-mehr"><Icon name="more" /></button>
          {ich && <span className="nicht-kompakt"><Benutzermenue ich={ich} mitName={false} /></span>}
        </div>
      </header>
      {/* Bereiche des Projekts als Reiter; die aktive Ansicht ist hervorgehoben */}
      <nav className="reiter" data-testid="reiter" aria-label="Bereiche des Projekts">
        <button className={ansicht.view === 'general' ? 'on' : ''} onClick={() => go({ view: 'general' })} title="Allgemeines: Einleitung, Kennzahlen, Version" data-testid="open-allgemeines"><Icon name="info" />Allgemeines</button>
        <button className={ansicht.view === 'map' || ansicht.view === 'process' ? 'on' : ''} onClick={() => go({ view: 'map' })} title="Prozesslandkarte" data-testid="open-landkarte"><Icon name="map" />Prozesse</button>
        <button className={ansicht.view === 'cockpit' ? 'on' : ''} onClick={() => go({ view: 'cockpit' })} title="Cockpit: Kennzahlen, Statusbericht, Besprechung importieren" data-testid="open-cockpit"><Icon name="chart" />Cockpit</button>
        <button className={ansicht.view === 'decisions' ? 'on' : ''} onClick={() => go({ view: 'decisions' })} title="Offene Entscheidungen" data-testid="open-decisions"><Icon name="decision" />Entscheidungen<Zahl n={openCount} farbe={z?.entscheidungen.ueberfaellig ? 'rot' : 'orange'} titel={z?.entscheidungen.ueberfaellig ? `${openCount} offen, ${z.entscheidungen.ueberfaellig} überfällig` : `${openCount} offen`} testid="zahl-entscheidungen" /></button>
        <button className={ansicht.view === 'tasks' ? 'on' : ''} onClick={() => go(ansicht.view === 'tasks' ? { ...ansicht, aid: null } : { view: 'tasks', aid: null, ansicht: 'liste' })} title="Aufgaben (Liste, Board, Gantt)" data-testid="open-aufgaben"><Icon name="check" />Aufgaben{z && <Zahl n={z.aufgaben.offen} farbe={z.aufgaben.ueberfaellig ? 'rot' : 'blau'} titel={`${z.aufgaben.offen} offen` + (z.aufgaben.ueberfaellig ? `, ${z.aufgaben.ueberfaellig} überfällig` : '')} testid="zahl-aufgaben" />}</button>
        <button className={ansicht.view === 'milestones' ? 'on' : ''} onClick={() => go({ view: 'milestones' })} title="Meilensteine und Phasen (Liste und Zeitleiste)" data-testid="open-meilensteine"><Icon name="target" />Meilensteine{z && <Zahl n={z.meilensteine.gesamt - z.meilensteine.erreicht} farbe={z.meilensteine.ueberfaellig.length ? 'rot' : 'blau'} titel={`${z.meilensteine.gesamt - z.meilensteine.erreicht} offen` + (z.meilensteine.ueberfaellig.length ? `, ${z.meilensteine.ueberfaellig.length} überfällig` : '')} testid="zahl-meilensteine" />}</button>
        {/* Reiter „Offene Punkte“ vorerst ausgeblendet (Thomas, 13.10.2026): Ansicht, Adresse /offene-punkte und Daten bleiben erhalten */}
        <button className={ansicht.view === 'tests' ? 'on' : ''} onClick={() => go({ view: 'tests' })} title="Tests: Testfälle je Prozessschritt" data-testid="open-tests"><Icon name="test" />Tests{z && <Zahl n={offeneTests} farbe={z.tests.fehler + z.tests.blockiert ? 'rot' : 'blau'} titel={`${z.tests.offen} offen` + (z.tests.fehler ? `, ${z.tests.fehler} mit Fehler` : '') + (z.tests.blockiert ? `, ${z.tests.blockiert} blockiert` : '')} testid="zahl-tests" />}</button>
        <button className={ansicht.view === 'checklisten' ? 'on' : ''} onClick={() => go({ view: 'checklisten' })} title="Checklisten: Go-Live, Schulung …" data-testid="open-checklisten"><Icon name="liste" />Checklisten{z && <Zahl n={z.checklisten.reduce((n, c) => n + c.gesamt - c.erledigt, 0)} farbe="blau" titel="offene Punkte in den Checklisten" testid="zahl-checklisten" />}</button>
        <button className={ansicht.view === 'abnahmen' ? 'on' : ''} onClick={() => go({ view: 'abnahmen' })} title="Abnahmen: Stand freigeben und Stände vergleichen" data-testid="open-abnahmen"><Icon name="abnahme" />Abnahmen</button>
        <button className={ansicht.view === 'dokumente' ? 'on' : ''} onClick={() => go({ view: 'dokumente' })} title="Dokumente: alle Dateien des Projekts – Schulungsunterlagen, Protokolle, Konzepte" data-testid="open-dokumente"><Icon name="folder" />Dokumente</button>
      </nav>
      {archiv && <ArchivHinweis archiv={archiv} onWiederherstellen={istAdmin ? () => void wiederherstellen() : undefined} />}
      {(ansicht.view === 'process' || zurueck) && (
        <nav className="crumbs" data-testid="crumbs" aria-label="Navigationspfad">
          <button onClick={() => go({ view: 'map' })}>Prozesslandkarte</button>
          {zurueck && ansicht.view !== 'tasks' && (
            // Gekommen aus einer Aufgabe: der Pfad führt über die Aufgabe, ein Klick auf sie springt zurück
            <>
              <span className="dot">›</span>
              <button onClick={() => go(zurueck.ansicht.view === 'tasks' ? { ...zurueck.ansicht, aid: null } : { view: 'tasks', aid: null, ansicht: 'liste' })}>Aufgaben</button>
              <span className="dot">›</span>
              <button className="aufgabe" onClick={() => go(zurueck.ansicht)} title="Zurück zur Aufgabe" data-testid="zurueck-zur-aufgabe">{zurueck.titel}</button>
            </>
          )}
          {sichtbareKrumen.map((p: Daten | null, i: number) => (
            <Fragment key={p?.id ?? 'mitte'}>
              <span className="dot">›</span>
              {!p ? <button className="mehr" title={'Ausgeblendete Stufen zeigen'} onClick={() => setKrumenAuf(pid)} data-testid="crumbs-mehr">…</button>
                : i === sichtbareKrumen.length - 1 ? <span className="cur">{p.titel}</span>
                : <button onClick={() => drill(p.id)}>{p.titel}</button>}
            </Fragment>
          ))}
          {proc && <button className="fruehere" onClick={() => { setVerlaufPid(proc.id); setDialog('history'); }} data-testid="fruehere-staende"
            title={'Frühere Stände dieses Prozesses\nZeigt, wie der Prozess in früheren Versionen aussah, und was sich seitdem geändert hat.'}>Frühere Stände</button>}
        </nav>
      )}
      <div className="main">
        {ansicht.view === 'cockpit' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Cockpit wird geladen …</div></section>}>
            <Cockpit projektId={projektId} data={data} />
          </Suspense>
        ) : ansicht.view === 'dokumente' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Dokumente werden geladen …</div></section>}>
            <DokumenteSeite projektId={projektId} data={data} suche={ansicht.suche ?? ''} setSuche={(suche) => go({ view: 'dokumente', suche }, true)}
              zuOrt={(typ, id) => { const a = bezugAnsicht(typ, id); if (!a) { toast('Das Ziel gibt es nicht mehr.', true); return; } go(a); if (a.view === 'process' && a.sel) setPanel(true); }} />
          </Suspense>
        ) : ansicht.view === 'checklisten' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Checklisten werden geladen …</div></section>}>
            <Checklisten projektId={projektId} />
          </Suspense>
        ) : ansicht.view === 'tests' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Tests werden geladen …</div></section>}>
            <Tests projektId={projektId} data={data} zuSchritt={(pid, sid) => go({ view: 'process', pid, sel: sid ? { type: 'node', id: sid } : null })} />
          </Suspense>
        ) : ansicht.view === 'stand' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Stand wird geladen …</div></section>}>
            <Stand projektId={projektId} data={data} nr={ansicht.nr} pid={ansicht.pid} go={(p) => go({ view: 'stand', nr: ansicht.nr, pid: p })} zumAktuellen={drill} />
          </Suspense>
        ) : ansicht.view === 'abnahmen' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Abnahmen werden geladen …</div></section>}>
            <Abnahmen projektId={projektId} data={data} />
          </Suspense>
        ) : ansicht.view === 'milestones' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Meilensteine werden geladen …</div></section>}>
            <Meilensteine projektId={projektId} />
          </Suspense>
        ) : ansicht.view === 'issues' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Offene Punkte werden geladen …</div></section>}>
            <OffenePunkte projektId={projektId} oid={ansicht.oid} go={(oid) => go({ view: 'issues', oid })}
              panelW={panelW} resizer={<PanelResizer onResize={resizePanel} />} />
          </Suspense>
        ) : ansicht.view === 'tasks' ? (
          <Suspense fallback={<section className="view"><div className="empty-state">Aufgaben werden geladen …</div></section>}>
            <Aufgaben key={aufgabenStand} projektId={projektId} data={data} aid={ansicht.aid} ansicht={ansicht.ansicht} zuBezug={zuBezug} suche={ansicht.suche ?? ''}
              optionen={ansicht.optionen ?? ''} setOptionen={(optionen) => go({ ...ansicht, optionen }, true)}
              ansichtUebernehmen={(a, optionen) => go({ ...ansicht, ansicht: a, optionen }, true)}
              go={(aid, a) => go({ ...ansicht, aid, ansicht: a })} setSuche={(suche) => go({ ...ansicht, suche }, true)} panelW={panelW} resizer={<PanelResizer onResize={resizePanel} />} />
          </Suspense>
        ) : <section className="view">{main}</section>}
        {proc && panel && <PanelResizer onResize={resizePanel} />}
        {proc && (
          <aside className={'panel' + (panel ? '' : ' closed')} style={panel ? { width: panelW + 'px' } : undefined}>
            {panel && <Panel data={data} pid={proc.id} sel={sel} edit={edit} onSelect={select} onDrill={jump} onNavigate={navigate} onClose={() => { setPanel(false); if (handy && sel) select(null); }} />}
          </aside>
        )}
        {proc && !panel && <button className="btn" style={{ position: 'absolute', right: 12, top: 70, zIndex: 6 }} onClick={() => setPanel(true)}>Details</button>}
      </div>
      {dialog === 'history' && <Suspense fallback={null}><HistoryDialog data={data} projektId={projektId} pid={verlaufPid} onNavigate={navigate}
        onStand={(nr) => { setDialog(null); go({ view: 'stand', nr, pid: verlaufPid ?? proc?.id ?? null }); }} onClose={() => setDialog(null)} /></Suspense>}
      {dialog === 'settings' && istAdmin && <Settings data={data} istAdmin={istAdmin && !archiv} onClose={() => setDialog(null)}
        archiv={{ archiviert: !!archiv, archivieren: () => void archivieren(), wiederherstellen: () => void wiederherstellen() }} />}
      {dialog === 'help' && <Suspense fallback={null}><HilfeDialog thema={hilfeThema} onClose={() => setDialog(null)} /></Suspense>}
      {nachFrage && <UngespeichertDialog weiter={() => { const w = nachFrage.weiter; setNachFrage(null); w(); }} abbrechen={() => setNachFrage(null)} />}
      {dialog === 'protokoll' && istAdmin && <Suspense fallback={null}><ProtokollDialog projektId={projektId} onClose={() => setDialog(null)} /></Suspense>}
      {dialog === 'asana' && istAdmin && <Suspense fallback={null}><AsanaImportDialog projektId={projektId} onClose={() => setDialog(null)}
        onFertig={() => { setAufgabenStand((n) => n + 1); go({ view: 'tasks', aid: null, ansicht: 'liste' }); }} /></Suspense>}
      {dialog === 'benachrichtigungen' && <Suspense fallback={null}><BenachrichtigungsEinstellungen onClose={() => setDialog(null)} /></Suspense>}
      {dialog === 'kalender' && <Suspense fallback={null}><KalenderAbo onClose={() => setDialog(null)} /></Suspense>}
      {dialog === 'mitglieder' && istAdmin && ich && <Suspense fallback={null}><MitgliederDialog projektId={projektId} titel={data.meta.titel} ich={ich} nurLesen={!!archiv} onClose={() => setDialog(null)} /></Suspense>}
    </div>
  );
}
